import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import {
  BulkMessaging as BulkMessagingImage,
  CallIntelligence as CallIntelligenceImage,
  DigitalForms as DigitalFormsImage,
  EmailMarketing as EmailMarketingImage,
  EmailMarketingPromo as EmailMarketingPromoImage,
  OnlineScheduling as OnlineSchedulingImage,
  PracticeAnalytics as PracticeAnalyticsImage,
  TextConnect as TextConnectImage,
  WeaveInsuranceVerification as WeaveInsuranceVerificationImage,
  WieFreeTrialSuccess as WieFreeTrialSuccessImage,
} from '@frontend/feature-subscription';
import { i18next } from '@frontend/i18n';
import { FeatureSignUpContentType } from '@frontend/product-subscription-sign-up-page';
import { InsuranceVerificationSignedUpContent } from '../components/FeatureSubscriptionPage/InsuranceVerificationContent/InsuranceVerificationSignedUpContent';

export const FeatureContentDataMap: Partial<Record<Feature, FeatureSignUpContentType['featureContent']>> = {
  [Feature.BULK_MESSAGING]: {
    title: i18next.t('Struggling to reach your contact base?', { ns: 'subscription>>bulk-messaging' }),
    subTitle: i18next.t('You need Weave Bulk Messaging.', { ns: 'subscription>>bulk-messaging' }),
    featureImage: BulkMessagingImage,
    videoUrl: 'https://fast.wistia.com/embed/medias/5pyfca7ws0',
    highlightedContent: i18next.t(
      "If you're still sending your patients messages one-at-a-time, then you need Weave Bulk Messaging.",
      { ns: 'subscription>>bulk-messaging' }
    ),
    contentText: i18next.t(
      "When you subscribe, you'll be able to compose and send texts to hundreds of recipients at once, allowing you to easily stay connected with more of your patients than ever before.",
      { ns: 'subscription>>bulk-messaging' }
    ),
  },
  [Feature.EMAIL_MARKETING]: {
    title: i18next.t("Create professional business emails effortlessly with Weave's simplified Email Marketing tool.", {
      ns: 'subscription>>email-marketing',
    }),
    featureImage: EmailMarketingImage,
    bannerImage: EmailMarketingPromoImage,
    videoUrl: '',
    highlightedContent: i18next.t(
      'Designed for non-experts, this tool empowers you to become a marketing pro without needing specialized skills.',
      { ns: 'subscription>>email-marketing' }
    ),
    contentText: i18next.t(
      "Access a library of pre-written templates and professional stock images to ensure your emails look polished and grab attention. Easily stay ahead by scheduling communications, including end-of-year sales, holiday greetings, and seasonal promotions to fuel your practice's growth. Weave's all-in-one Email Marketing tool simplifies your marketing efforts, enabling you to create and send professional emails seamlessly.",
      { ns: 'subscription>>email-marketing' }
    ),
  },
  [Feature.ONLINE_SCHEDULING]: {
    title: i18next.t(
      'Let your website be an extension of your staff around the clock by letting your customers request appointments with Weave Online Scheduling',
      { ns: 'subscription>>online-scheduling' }
    ),
    featureImage: OnlineSchedulingImage,
    videoUrl: 'https://fast.wistia.com/embed/medias/xa4iqfutw1',
    highlightedContent: i18next.t(
      'Online Scheduling gives your customers a way to schedule appointments from your website, allowing you to capture more business each day.',
      { ns: 'subscription>>online-scheduling' }
    ),
    contentText: i18next.t(
      'You will be able to reduce scheduling phone calls and gives your business the flexibility to respond when it is convenient for you',
      { ns: 'subscription>>online-scheduling' }
    ),
  },
  [Feature.TEXT_CONNECT]: {
    title: i18next.t(
      "Let customers start a conversation from your webpage and respond when it's convenient for them.",
      { ns: 'subscription>>text-connect' }
    ),
    featureImage: TextConnectImage,
    videoUrl: 'https://fast.wistia.com/embed/medias/9prvpgkr7l',
    highlightedContent: i18next.t(
      'Weave Text Connect lets visitors to your website start a conversation in a chat box and then transfer it to a text message thread on their phones.',
      { ns: 'subscription>>text-connect' }
    ),
    contentText: i18next.t(
      'Unlike traditional web chats, Text Connect allows customers to continue the conversation without being chained to a computer. Customers simply start by entering their contact information and a short message. Then the whole conversation transfers to a text message, and your office can respond through the Weave desktop app or mobile app. Text Connect provides customers convenience to respond whenever and wherever, while ensuring your office staff can give customers their full attention.',
      { ns: 'subscription>>text-connect' }
    ),
  },
  [Feature.FORMS]: {
    title: i18next.t('Get necessary patient data quickly and easily.', { ns: 'subscription>>forms' }),
    subTitle: i18next.t('Forms are just the beginning.', { ns: 'subscription>>forms' }),
    featureImage: DigitalFormsImage,
    videoUrl: 'https://fast.wistia.com/embed/medias/2mcdggqs2z',
    contentText: i18next.t(
      'Weave Digital Forms is the paperless forms solution that helps your business run more efficiently by automating every step of the patient intake process—from creating forms, to sending forms, to recording patient information. Digital Forms helps you do it all faster so you can focus on providing patients world-class service.',
      { ns: 'subscription>>forms' }
    ),
  },
  [Feature.PRACTICE_ANALYTICS]: {
    title: i18next.t('Better understand your practice.', { ns: 'subscription>>practice-analytics' }),
    subTitle: i18next.t('Analyze data, schedule more patients, and increase revenue opportunities.', {
      ns: 'subscription>>practice-analytics',
    }),
    featureImage: PracticeAnalyticsImage,
    videoUrl: 'https://fast.wistia.com/embed/medias/ymxr76flgk',
    highlightedContent: i18next.t(
      'Practice Analytics tackles challenges like limited visibility into abandoned treatment plans, inefficient appointment scheduling and lack of performance tracking.',
      { ns: 'subscription>>practice-analytics' }
    ),
    contentText: i18next.t(
      'It gives you real-time actionable data built into your communication platform to help drive practice growth, team performance, and daily production.',
      { ns: 'subscription>>practice-analytics' }
    ),
  },
  [Feature.CALL_INTELLIGENCE]: {
    title: i18next.t(
      "With Call Intelligence, Weave's AI powered platform, get deeper insights into what each patient is calling about, a list of missed booking opportunities you can follow up on, and the sentiment of participants during each call - thereby unlocking growth opportunities for your office.",
      { ns: 'subscription>>call-intel' }
    ),
    featureImage: CallIntelligenceImage,
    videoUrl: 'https://fast.wistia.com/embed/medias/00q24rsmga',
    contentText: i18next.t(
      'Call Intelligence analyzes all incoming and outgoing calls to your practice, providing full transcriptions and AI-generated summaries for quick and easy review. It identifies missed revenue opportunities, detects new patients, categorizes calls (billing, complaints, cancellations, etc.), and helps train staff by identifying areas to improve patient interactions and sentiment. By comparing performance across multiple locations, Call Intelligence helps offices improve patient experience, optimize training, and drive revenue growth.',
      { ns: 'subscription>>call-intel' }
    ),
  },
  [Feature.WEAVE_VERIFY]: {
    title: i18next.t(
      'With Weave Insurance Verification, your staff can focus on taking care of patients in your office and getting them to accept the recommended treatment plan, not spending hours on the phone or switching between different systems to verify dental insurance.',
      { ns: 'subscription>>wiv' }
    ),
    featureImage: WeaveInsuranceVerificationImage,
    videoUrl: 'https://fast.wistia.com/embed/medias/p6om5icgjx',
    contentText: i18next.t(
      'Verifying insurance eligibility is time consuming and complex. Not only does your staff have to contact patients to get the correct membership information, then they have to wait on hold with the insurance company to verify eligibility. Plus, think about the times when you recommend a treatment plan and need to know whether or how much a patient’s insurance will pay. Even if you have software in place to verify insurance eligibility, you still have to switch between software systems to make it all work.',
      { ns: 'subscription>>wiv' }
    ),
  },
  [Feature.ENHANCED_INSURANCE_VERIFICATION]: {
    title: i18next.t('Reduce claim denials and simplify your workflow with detailed, accurate insurance eligibility.', {
      ns: 'subscription>>eiv',
    }),
    featureImage: WieFreeTrialSuccessImage,
    videoUrl: 'https://fast.wistia.com/embed/medias/s9gfc55at3?autoplay=1',
    contentText: i18next.t(
      'Spend less time manually searching dental insurance websites with Weave Insurance Eligibility. Subscribe to get a more accurate and comprehensive picture of insurance coverage you can get paid faster.',
      { ns: 'subscription>>eiv' }
    ),
  },
  [Feature.TRUELARK_AI_VOICE]: {
    title: i18next.t('Weave AI Receptionist is an AI-powered teammate for your front desk', {
      ns: 'subscription>>ai-receptionist',
    }),
    featureImage: BulkMessagingImage, // TODO: Add AIR Receptionist image
    videoUrl: 'https://fast.wistia.com/embed/medias/t6lmm4ntjk',
    contentText: i18next.t(
      "It's your always-on teammate, built to live right inside the Weave platform. Weave AI Receptionist solves the headache of missed calls and silent voicemails by instantly handling patient inquiries, answering FAQs, and booking appointments—24/7. Whether it's via Voice or Messaging, your practice stays open even when your doors are closed.",
      { ns: 'subscription>>ai-receptionist' }
    ),
  },
};

export const FeatureActivatedContentDataMap: Partial<Record<Feature, FeatureSignUpContentType['featureContent']>> = {
  [Feature.WEAVE_VERIFY]: {
    ...(FeatureContentDataMap[Feature.WEAVE_VERIFY] as FeatureSignUpContentType['featureContent']),
    contentText: i18next.t('You have successfully signed-up for Weave Insurance Verification.', {
      ns: 'subscription>>wiv',
    }),
    customContent: <InsuranceVerificationSignedUpContent />,
  },
};

export const FeatureSubscriptionCardDataMap: Partial<
  Record<Feature, FeatureSignUpContentType['subscriptionCardContent']>
> = {
  [Feature.ONLINE_SCHEDULING]: {
    title: i18next.t('Keep a fuller schedule, automatically', { ns: 'subscription>>online-scheduling' }),
    featurePoints: [
      i18next.t("Scheduling power in your customer's hands", { ns: 'subscription>>online-scheduling' }),
      i18next.t('Respond when you want to', { ns: 'subscription>>online-scheduling' }),
      i18next.t('Book from your own website', { ns: 'subscription>>online-scheduling' }),
    ],
  },
  [Feature.TEXT_CONNECT]: {
    title: i18next.t('Your office, just a text away, night or day', { ns: 'subscription>>text-connect' }),
    featurePoints: [
      i18next.t('Allow customers to start a text conversation from your website', { ns: 'subscription>>text-connect' }),
      i18next.t('Respond when it is convenient', { ns: 'subscription>>text-connect' }),
      i18next.t('Available 24/7 on your website', { ns: 'subscription>>text-connect' }),
    ],
  },
  [Feature.FORMS]: {
    title: i18next.t('Automate every part of information collection', { ns: 'subscription>>forms' }),
    featurePoints: [
      i18next.t('Use templates or our digitization service to create forms', { ns: 'subscription>>forms' }),
      i18next.t('Send forms automatically with Weave communication features', { ns: 'subscription>>forms' }),
      i18next.t('Write back form data to the patient file (available with certain practice management systems)', {
        ns: 'subscription>>forms',
      }),
    ],
  },
  [Feature.PRACTICE_ANALYTICS]: {
    title: i18next.t('Practice Analytics Can Help You', { ns: 'subscription>>practice-analytics' }),
    featurePoints: [
      i18next.t('Understand the health of your practice', { ns: 'subscription>>practice-analytics' }),
      i18next.t('Identify patients slipping through the cracks', { ns: 'subscription>>practice-analytics' }),
      i18next.t('Increase practice production and revenue', { ns: 'subscription>>practice-analytics' }),
      i18next.t('Provide actionable data to team members', { ns: 'subscription>>practice-analytics' }),
    ],
  },
  [Feature.CALL_INTELLIGENCE]: {
    title: i18next.t(
      'With Call Intelligence, unlock higher revenue each month and improve overall performance of your office location(s)',
      { ns: 'subscription>>call-intel' }
    ),
    featurePoints: [
      i18next.t('Full transcript + summary of each call', { ns: 'subscription>>call-intel' }),
      i18next.t('Missed revenue opportunity identification', { ns: 'subscription>>call-intel' }),
      i18next.t('Call categories + appointment types', { ns: 'subscription>>call-intel' }),
      i18next.t('Customer + staff sentiment analysis', { ns: 'subscription>>call-intel' }),
    ],
  },
  [Feature.WEAVE_VERIFY]: {
    title: i18next.t('Subscribe to Weave Insurance Verification', { ns: 'subscription>>wiv' }),
    featurePoints: [
      i18next.t(
        "Weave Insurance Verification patient's insurance eligibility with a single click from either your calendar or patient insights application",
        { ns: 'subscription>>wiv' }
      ),
      i18next.t('Get a full insurance report including any information you might need to treat your patients', {
        ns: 'subscription>>wiv',
      }),
      i18next.t(
        'Contact patients directly from Weave to get more information without switching to a different system',
        { ns: 'subscription>>wiv' }
      ),
    ],
  },
  [Feature.ENHANCED_INSURANCE_VERIFICATION]: {
    title: i18next.t('Get more detailed insurance reports', { ns: 'subscription>>eiv' }),
    featurePoints: [
      i18next.t('More data sources', {
        ns: 'subscription>>eiv',
      }),
      i18next.t('Direct payer portal lookups when available', {
        ns: 'subscription>>eiv',
      }),
      i18next.t('Standardized, easy-to-read reports', { ns: 'subscription>>eiv' }),
      i18next.t('Now supports Primary & Secondary Insurance', { ns: 'subscription>>eiv' }),
      i18next.t('View insurance status directly on the Schedule', { ns: 'subscription>>eiv' }),
    ],
  },
};

export const FeatureActivatedSubscriptionCardDataMap: Partial<
  Record<Feature, FeatureSignUpContentType['subscriptionCardContent']>
> = {
  [Feature.WEAVE_VERIFY]: {
    ...(FeatureSubscriptionCardDataMap[Feature.WEAVE_VERIFY] as FeatureSignUpContentType['subscriptionCardContent']),
    title: i18next.t('You have subscribed to Weave Insurance Verification', { ns: 'subscription>>wiv' }),
  },
};

export const FeatureSubscriptionDataMap: Partial<Record<Feature, FeatureSignUpContentType['subscriptionData']>> = {
  [Feature.ONLINE_SCHEDULING]: {
    price: 49,
    subscriptionTerm: 'monthly',
  },
  [Feature.TEXT_CONNECT]: {
    price: 29,
    subscriptionTerm: 'monthly',
  },
  [Feature.FORMS]: {
    price: 199,
    subscriptionTerm: 'monthly',
  },
  [Feature.PRACTICE_ANALYTICS]: {
    price: 199,
    subscriptionTerm: 'monthly',
  },
  [Feature.WEAVE_VERIFY]: {
    price: 69,
    subscriptionTerm: 'monthly',
  },
  [Feature.ENHANCED_INSURANCE_VERIFICATION]: {
    price: 249,
    subscriptionTerm: 'monthly',
  },
  [Feature.TRUELARK_AI_VOICE]: {
    price: 199,
    subscriptionTerm: 'monthly',
  },
  [Feature.CALL_INTELLIGENCE]: {
    price: 199,
    subscriptionTerm: 'monthly',
  },
};

export const FeaturePermissionDataMap: Partial<Record<Feature, FeatureSignUpContentType['permissions']>> = {
  [Feature.ONLINE_SCHEDULING]: {
    trialAllowed: true,
    selfSubscribeAllowed: true,
    allowedForDentalOnly: false,
  },
  [Feature.TEXT_CONNECT]: {
    trialAllowed: true,
    selfSubscribeAllowed: true,
    allowedForDentalOnly: false,
  },
  [Feature.FORMS]: {
    trialAllowed: true,
    selfSubscribeAllowed: true,
    allowedForDentalOnly: false,
  },
  [Feature.PRACTICE_ANALYTICS]: {
    trialAllowed: true,
    selfSubscribeAllowed: true,
    allowedForDentalOnly: false,
  },
  [Feature.CALL_INTELLIGENCE]: {
    trialAllowed: true,
    selfSubscribeAllowed: true,
    allowedForDentalOnly: false,
  },
  [Feature.WEAVE_VERIFY]: {
    trialAllowed: true,
    selfSubscribeAllowed: true,
    allowedForDentalOnly: true,
  },
  [Feature.ENHANCED_INSURANCE_VERIFICATION]: {
    trialAllowed: true,
    selfSubscribeAllowed: true,
    allowedForDentalOnly: false,
  },
};
